














































































































































































































































































import store, { Dataset, KPI, PastureKeys } from "../store";
import { Vue, Component } from "vue-property-decorator";
import { monthShortNames, MonthsInOrder } from "../common/dates";
import { colors } from "../common/colors";
import { detectTouchScreen } from "../mixins/mixins";
import { platform, print } from "../mla-shared/";
import Calculations from "./calculations/AllCalculations.vue";
import pattern from 'patternomaly';
import breeds from "@/data/breeds";

const FeedProps = Vue.extend({
	props: {
		type: {
			type: String
		}
	},
	components: {
		Calculations
	},
	store
});

@Component
export default class Feed extends FeedProps {
	calculations: any = null;
	loading: boolean = false;
	isTouchscreen: boolean = detectTouchScreen();
	plotType: string = "tonnes / ha";
	plotTypeItems = ["tonnes / ha", "tonnes/effective grazing area (total ha)"];
	chartType: string = "Bar";
	chartTypeItems = ["Bar", "Line", "Radar"];
	update: number = 0;
	pastureSupply = store.state.supply || {};
	demandHidden: boolean = false;
	supplyHidden: boolean = false;
	utilizationMessages = {
		demandGreaterThanMax:
			"The high utilization of this pasture may lead to environmental damage. Consider ways to boost pasture growth or reduce stock demand.",
		fooGreaterThanRecMinFoo:
			"The predicted Feed On Offer drops below recommended minimum values. This could damage soils and pastures and reduce animal performance. Consider changing pasture and animal management to increase FOO at critical times."
	};
	maxUtilizationRates: any = {
		Clay: { level: 70, undulating: 65, steep: 50 },
		"Clay Loam": { level: 65, undulating: 60, steep: 50 },
		"Sandy Loam": { level: 60, undulating: 55, steep: 45 },
		Sand: { level: 50, undulating: 45, steep: 40 }
	};
	recommendedMinimumFoo: any = {
		Clay: { level: 800, undulating: 1000, steep: 1200 },
		"Clay Loam": { level: 800, undulating: 1000, steep: 1200 },
		"Sandy Loam": { level: 1000, undulating: 1200, steep: 1500 },
		Sand: { level: 1000, undulating: 1200, steep: 1500 }
	};
	kpi: KPI[] = [
		{
			id: "pastureDeficitFresh",
			indicator: "Pasture deficit, using freshly grown supply",
			value: 0,
			unit: "tonnes/ha/year",
			desc:
				"The total weight of pasture drymatter summed across the months where livestock demands exceed pasture supply assuming that no excess pasture is carried over from one month to the next."
		},
		{
			id: "pastureDeficitCarryover",
			indicator: "Pasture deficit, using supply with carryover",
			value: 0,
			unit: "tonnes/ha/year",
			desc:
				'The total weight of pasture drymatter summed across the months where livestock demands exceed pasture supply assuming that (depending on your "wastage" settings) around 2/3 of pasture that is in excess of livestock demands is carried over from one month to the next.'
		},
		{
			id: "liveweightCattle",
			indicator: "Liveweight produced, cattle",
			value: 0,
			unit: "tonnes",
			desc:
				"The difference between the cattle liveweight at the end of the 12 months less that at the start, summed across all livestock classes, plus the sum of the difference between all sale weights and purchase weights for all traded stock."
		},
		{
			id: "liveweightCattlePerHa",
			indicator: "Liveweight produced per ha allocated to cattle ",
			value: 0,
			unit: "kg/ha/year",
			desc:
				"The total cattle liveweight produced divided by hectares allocated to the cattle enterprise."
		},
		{
			id: "liveweightSheep",
			indicator: "Liveweight produced, sheep",
			value: 0,
			unit: "tonnes",
			desc:
				"The difference between the sheep liveweight at the end of the 12 months less that at the start, summed across all livestock classes, plus the sum of the difference between all sale weights and purchase weights for all traded stock."
		},
		{
			id: "liveweightSheepPerHa",
			indicator: "Liveweight produced per ha allocated to sheep",
			value: 0,
			unit: "kg/ha/year",
			desc:
				"The total sheep liveweight produced divided by hectares allocated to the sheep enterprise."
		},
		{
			id: "pastureDemand",
			indicator: "Pasture demand as a % of pasture grown",
			value: 0,
			unit: "%",
			desc:
				"The total drymatter demand for all the livestock for the year divided by the total weight of pasture drymatter grown for the year multipled by 100."
		},
		{
			id: "minimumFeedOnOffer",
			indicator: "Minimum Feed On Offer",
			value: 0,
			unit: "kg DM/ha",
			desc:
				"The minimum amount of total feed on offer (freshly grown supply + carryover + supplement) during the course of the year. If this value falls below 800 kg/ha, it could be damaging to soils and pastures."
		}
	];

	get enterprise() {
		return this.$store.state.pasture.simulation.enterprise.toLowerCase().includes('cattle') ? 'cattle' : 'sheep'
	}

	// Return calculations component reference
	get calcRef(): Calculations {
		if (this.enterprise === 'cattle') {
			return this.$refs.cattleCalcs as Calculations;
		} else {
			return this.$refs.sheepCalcs as Calculations;
		}
	}

	get cattleCalcs() {
		return store.getters.calculations['cattle']
	}

	get sheepCalcs() {
		return store.getters.calculations['sheep']
	}

	// Return month in correct order from start date
	get warnings() {
		let warnings: string[] = [];
		if (
			store.state.pasture &&
			store.state.pasture.simulation &&
			store.state.pasture.simulation.soil &&
			store.state.pasture.simulation.slope
		) {
			if (
				this.kpi.filter(kpi => kpi.id === "pastureDemand")[0].value >
				this.maxUtilizationRates[store.state.pasture.simulation.soil][
					store.state.pasture.simulation.slope.toLowerCase()
				]
			) {
				warnings.push(this.utilizationMessages.demandGreaterThanMax);
			}
			if (
				this.kpi.filter(kpi => kpi.id === "minimumFeedOnOffer")[0]
					.value <
				this.recommendedMinimumFoo[store.state.pasture.simulation.soil][
					store.state.pasture.simulation.slope.toLowerCase()
				]
			) {
				warnings.push(this.utilizationMessages.fooGreaterThanRecMinFoo);
			}
		}
		return warnings;
	}

	// Return chart options prop
	get chartOptions() {
		// Get the max value for ticks
		let max = this.getMax(this.type === "demand" ? this.demandData : this.supplyData, 0.005);
		// Add 5% padding to top of chart
		// max = max + max * 0.05;

		return {
			responsive: true,
			maintainAspectRatio: true,
			aspectRatio: this.$vuetify.breakpoint.width < 768 ? 1.5 : 3,
			resizeDelay: 1000,
			interaction: {
				mode: "index",
				intersect: false
			},
			scales: {
				x: {
					display: this.chartType !== "Radar",
					position: "bottom",
					stacked: true,
					beginAtZero: true,
					title: {
						display: true,
						text: "Month",
						color: "#000",
						font: {
							size: 14
						},
						padding: {
							top: 15
						}
					},
					ticks: {
						color: "#000"
					},
					grid: {
						color: "rgba(0,0,0,0.05)",
						tickColor: "#000",
						borderColor: "#000"
					}
				},
				yDemand: {
          display: this.type === "demand" && this.chartType !== "Radar",
          type: "linear",
          position: "left",
					beginAtZero: true,
					grace: "2%",
          suggestedMax: max,
          ticks: {
            precision: !this.demandHidden ? 0 : 4
          },
					grid: {
						color: "rgba(0,0,0,0.05)",
						tickColor: "#000",
						borderColor: "#000"
					},
          title: {
						display: true,
						text:
              "Pasture " +
              (this.plotType === "tonnes / ha"
                ? "(tonnes DM/ha)"
                : "(tonnes DM)"),
						color: "#000",
						font: {
							size: 14
						},
						padding: {
							bottom: 15
						}
					}
        },
        ySupply: {
          display: this.type === "supply" && this.chartType !== "Radar",
          type: "linear",
          position: "left",
					beginAtZero: true,
					grace: "5%",
          suggestedMax: max,
					grid: {
						color: "rgba(0,0,0,0.05)",
						tickColor: "#000",
						borderColor: "#000"
					},
          title: {
						display: true,
						text:
              "Pasture " +
              (this.plotType === "tonnes / ha"
                ? "(tonnes DM/ha)"
                : "(tonnes DM)"),
						color: "#000",
						font: {
							size: 14
						},
						padding: {
							bottom: 15
						}
					}
        }
			},
			plugins: {
				chartAreaBorder: {
					borderColor: "rgba(0,0,0,0.1)",
					borderWidth: 1
				},
				tooltip: {
					filter: function(tooltipItem: any) {
						return tooltipItem.dataset.pointStyle != "triangle" && tooltipItem.raw > 0;
					},
					callbacks: {
						label: function(context: any) {
							let yLabel: string = "";
							if (context.parsed.y !== null) {
								yLabel += parseFloat(
									context.parsed.y.toFixed(2)
								);
							}
							if (context.raw > 0) {
								return context.dataset.label + ": " + yLabel;
							}
						},
						labelColor: function(context: any) {
							let dataset =
								context.chart.config.data.datasets[
									context.datasetIndex
								];
							return {
								borderColor:
									dataset.backgroundColor != "transparent"
										? dataset.backgroundColor
										: dataset.borderColor,
								backgroundColor:
									dataset.backgroundColor != "transparent"
										? dataset.backgroundColor
										: dataset.borderColor
							};
						},
						footer: (tooltipItems:any) => {
							let total:number = 0;
							if (this.type === 'demand') {
							tooltipItems
								.filter((ti:any) => !ti.dataset.label.toLowerCase().includes("total") && !ti.dataset.label.toLowerCase().includes("fresh"))
								.map((a:any) => { total += a.raw });
							} else {
								tooltipItems
								.filter((ti:any) => !ti.dataset.label.toLowerCase().includes("total"))
								.map((a:any) => { total += a.raw });
							}
							return 'Total feed ' + this.type + ': ' + parseFloat(total.toFixed(2));
						}
					}
				},
				legend: {
					display: true,
					position: "top",
					labels: {
						usePointStyle: true,
						boxWidth: 12,
						color: "#000",
						font: {
							size: 16
						},
						filter: function(legendItem: any, chart:any) {
							let sum:number = 0
							if (chart.datasets[legendItem.datasetIndex].data.length > 0) {
								sum = chart.datasets[legendItem.datasetIndex].data.reduce((a:number, b:number) => a + b)
							}
							if (!legendItem.text.includes("ignore") && sum != 0) {
								return legendItem.text;
							}
						},
						generateLabels: function(chart: any) {
							var data = chart.data;
							var legends = Array.isArray(data.datasets)
								? data.datasets.map(function(
										dataset: any,
										i: number
								  ) {
										return {
											text: dataset.label,
											datasetIndex: i,
											fillStyle: dataset.borderDash
												? "transparent"
												: dataset.backgroundColor,
											hidden: !chart.isDatasetVisible(i),
											pointStyle: "rect",
											borderRadius: 0
										};
								  },
								  this)
								: [];
							return legends;
						}
					}
				}
			}
		};
	}

	// Return month in correct order from start date
	get monthsInOrder() {
		return MonthsInOrder(store.state.pasture!.simulation.date).map(m => ({
			name: monthShortNames[m - 1],
			month: m
		}));
	}

	// Return pasture keys
	get pastureKeys() {
		if (this.pastureSupply) {
			return Object.keys(this.pastureSupply)
				.filter((e: any) => e.substr(0, 1) === "p")
				.map(p => p as PastureKeys);
		} else {
			return [];
		}
	}

	// Return feed demand chart data
	get demandData() {
		let datasets: Dataset[] = [];
		const pasture = store.state.cattle
			? this.cattleCalcs.pasture
			: this.sheepCalcs.pasture;
		const supplement = store.state.cattle
			? this.cattleCalcs.supplement.tonnesMonth
			: this.sheepCalcs.supplement.tonnesMonth;
		const carryover = store.state.cattle
			? this.cattleCalcs.carryover
			: this.sheepCalcs.carryover;

		// Total Feed On Offer Line
		let totalAvailable = pasture.total.tonnesMonth.map(
			(tm: number, month: number) => {
				return tm + carryover.total[month];
			}
		);

		datasets.push({
			yAxisID: "yDemand",
			type: this.chartType !== "Radar" ? "line" : "radar",
			label: "Total feed on offer",
			backgroundColor: colors.other["Total feed on offer"],
			borderColor: colors.other["Total feed on offer"],
			pointStyle: 'rect',
      pointBorderColor: 'rgba(0, 0, 0, 0.5)',
			data: totalAvailable.map((ta: number, index: number) => {
				if (this.plotType === "tonnes/effective grazing area (total ha)") {
					return (ta + supplement[index]);
				} else {
					let divisor = pasture.total.area[index];
					return (ta + supplement[index]) / (divisor > 0 ? divisor : 1);
				}
			}),
			fill: false,
			lineTension: 0,
			order: 5,
			hidden: false
		});

		// Freshly Grown Supply Line
		let freshSupply = pasture.total.tonnesMonth.map(
			(tm: number, index: number) => {
				if (this.plotType === "tonnes/effective grazing area (total ha)") {
					return tm
				} else {
					let divisor = pasture.total.area[index]
					return tm / (divisor > 0 ? divisor : 1);
				}
			}
		);
		datasets.push({
			yAxisID: "yDemand",
			type: this.chartType === "Radar" ? "radar" : "line",
			label: "Fresh grown supply",
			backgroundColor: colors.other["Fresh grown supply"],
			borderColor: colors.other["Fresh grown supply"],
			pointStyle: 'rect',
			pointBorderColor: 'rgba(0, 0, 0, 0.5)',
			data: freshSupply,
			fill: false,
			lineTension: 0,
			order: 0,
			hidden: false
		});

		// Freshly Grown Supply + Supplement Line
		let freshSupplySupplement = pasture.total.tonnesMonth.map(
			(tm: number, index: number) => {
				if (this.plotType === "tonnes/effective grazing area (total ha)") {
					return (tm + supplement[index]) 
				} else {
					let divisor = pasture.total.area[index]
					return (tm + supplement[index]) / (divisor > 0 ? divisor : 1);
				}
			}
		);
		datasets.push({
			yAxisID: "yDemand",
			type: this.chartType === "Radar" ? "radar" : "line",
			label: "Fresh supply + supplement",
			backgroundColor: colors.other["Fresh supply + supplement"],
			borderColor: colors.other["Fresh supply + supplement"],
			pointStyle: 'rect',
			pointBorderColor: 'rgba(0, 0, 0, 0.5)',
			data: freshSupplySupplement,
			fill: false,
			lineTension: 0,
			order: 0,
			hidden: false
		});

		// Cattle Tonnes DM / ha
		if (
			store.state
				.pasture!.simulation.enterprise!.toLowerCase()
				.includes("cattle")
		) {
			if (this.cattleCalcs.totalDmReqsKeyValues) {
				Object.keys(
					this.cattleCalcs.totalDmReqsKeyValues
				).map((key: string, index: number) => {
					datasets.push({
						yAxisID: "yDemand",
						label: key,
						backgroundColor: colors.cattle[key],
						borderColor: 'black', // colors.cattle[key],
						borderWidth: 0.5,
						pointStyle: 'rect',
      			pointBorderColor: 'rgba(0, 0, 0, 0.5)',
						data: this.cattleCalcs.totalDmReqsKeyValues[
							key
						].map((val: number, index: number) => {
							if (val >= 0) {
								if (this.plotType === "tonnes/effective grazing area (total ha)") {
									return val
								} else {
									let divisor = pasture.total.area[index]
									return val / (divisor > 0 ? divisor : 1);
								}
							} else {
								return 0
							}
						}),
						fill: false,
						lineTension: 0,
						order: breeds.cattle.classes.breeding.indexOf(key as any) != -1 ? 10 : key.toLowerCase().includes('unweaned') ? 20 : 30,
						hidden: false,
						stack: 'combined'
					});
				});
			}
		}

		// Sheep Tonnes DM / ha
		if (
			store.state
				.pasture!.simulation.enterprise!.toLowerCase()
				.includes("sheep")
		) {
			if (this.sheepCalcs.totalDmReqsKeyValues) {
				Object.keys(
					this.sheepCalcs.totalDmReqsKeyValues
				).map((key: string, index: number) => {
					datasets.push({
						yAxisID: "yDemand",
						label: key,
						backgroundColor: colors.sheep[key],
						borderColor: 'black', // colors.sheep[key],
						borderWidth: 0.5,
						pointStyle: 'rect',
						pointBorderColor: 'rgba(0, 0, 0, 0.5)',
						data: this.sheepCalcs.totalDmReqsKeyValues[
							key
						].map((val: number, index: number) => {
							if (val >= 0) {
								if (this.plotType === "tonnes/effective grazing area (total ha)") {
									return val
								} else {
									let divisor = pasture.total.area[index]
									return val / (divisor > 0 ? divisor : 1);
								}
							} else {
								return 0
							}
						}),
						fill: false,
						lineTension: 0,
						order: breeds.sheep.classes.breeding.indexOf(key as any) != -1 ? 40 : key.toLowerCase().includes('unweaned') ? 50 : 60,
						hidden: false,
						stack: 'combined'
					});
				});
			}
		}

		return {
			labels: this.monthsInOrder.map(m => m.name),
			datasets: datasets
		};
	}

	// Return feed supply chart data
	get supplyData() {
		let datasets: Dataset[] = [];
		const pasture = store.state.cattle
			? this.cattleCalcs.pasture
			: this.sheepCalcs.pasture;

		// Total feed demand
		const tfdData = store.state.cattle
			? this.cattleCalcs.totalDmCombinedEnterprises
			: this.sheepCalcs.totalDmCombinedEnterprises;
		datasets.push({
			type: this.chartType === "Radar" ? "radar" : "line",
			yAxisID: "ySupply",
			label: "Total feed demand",
			backgroundColor: colors.other["Total feed demand"],
			borderColor: colors.other["Total feed demand"],
			pointStyle: 'rect',
			pointBorderColor: 'rgba(0, 0, 0, 0.5)',
			data: tfdData.map((tfd: number, index: number) => {
				if (this.plotType === "tonnes/effective grazing area (total ha)") {
					return tfd
				} else {
					let divisor = pasture.total.area[index]
					return tfd / (divisor > 0 ? divisor : 1);
				}
			}),
			fill: false,
			lineTension: 0,
			order: 0,
			hidden: false
		});

		// Pastures

		if (
			store.state.types &&
			store.state.pasture!.simulation.growthRateSource ===
				"Add Pasture Types"
		) {
			// let types = store.state.types
			for (let i = 0; i < this.pastureKeys.length; i++) {
				const color = this.randomColor();
				const freshSupplyColour = color.format({
					...color,
					a: 0.7
				});
				const carryOverColour = pattern.draw(
					'diagonal',
					color.format({
						...color,
						a: 0.5
					})
				);

				const pastureKey = this.pastureKeys[i] as PastureKeys;
				const freshSupply = this.pastureSupply[pastureKey].tonnesDM;
				const carryOver = store.state.cattle
					? this.cattleCalcs.carryover.pastures[
							pastureKey
					  ]
					: this.sheepCalcs.carryover.pastures[
							pastureKey
					  ];
				let label = this.pastureSupply[this.pastureKeys[i]].pasture;

				// Use custom labels if they exist
				if (store.state.pasture!.simulation.pastures[i].label) {
					label = store.state.pasture!.simulation.pastures[i]
						.label as string;
				}

				// Pasture Fresh Supply (Pasture Type)
				datasets.push({
					yAxisID: "ySupply",
					label,
					backgroundColor: freshSupplyColour,
					borderColor: 'black', // freshSupplyColour,
					borderWidth: 0.5,
					pointStyle: 'rect',
      		pointBorderColor: 'rgba(0, 0, 0, 0.5)',
					data: this.monthsInOrder.map((mx, month: number) => {
						if (this.plotType === "tonnes/effective grazing area (total ha)") {
							return freshSupply[mx.month - 1];
						} else {
							let divisor = pasture.total.area[month];
							return freshSupply[mx.month - 1] / (divisor > 0 ? divisor : 1);
						}
					}),
					fill: false,
					lineTension: 0,
					order: 1,
					hidden: false,
					stack: 'combine'
				});

				// Pasture Carryover (Pasture Type)
				datasets.push({
					yAxisID: "ySupply",
					label: `Carryover: ${label}`,
					backgroundColor: carryOverColour,
					borderColor: 'black', // carryOverColour,
					borderWidth: 0.5,
					pointStyle: 'rect',
      		pointBorderColor: 'rgba(0, 0, 0, 0.5)',
					data: carryOver.map((co: number, month: number) => {
						if (this.plotType === "tonnes/effective grazing area (total ha)") {
							return co;
						} else {
							let divisor = pasture.total.area[month];
							return co / (divisor > 0 ? divisor : 1);
						}
					}),
					fill: false,
					lineTension: 0,
					order: 1,
					hidden: false,
					stack: 'combine'
				});

				store.state.supply![pastureKey].carryOver =
					datasets[datasets.length - 1].data;
			}
		} else {
			const freshSupply = store.state.cattle
				? this.cattleCalcs.pasture.total
				: this.sheepCalcs.pasture.total;
			const carryOver = store.state.cattle
				? this.cattleCalcs.carryover.total
				: this.sheepCalcs.carryover.total;

			// Pasture Fresh Supply (Total)
			datasets.push({
				yAxisID: "ySupply",
				label: "Freshly grown supply",
				backgroundColor: colors.other["Fresh grown supply"],
				borderColor: 'black', // colors.other["Fresh grown supply"],
				borderWidth: 0.5,
				pointStyle: 'rect',
      	pointBorderColor: 'rgba(0, 0, 0, 0.5)',
				data: freshSupply.tonnesMonth.map(
					(tm: number, month: number) => {
						if (this.plotType === "tonnes/effective grazing area (total ha)") {
							return tm;
						} else {
							let divisor = pasture.total.area[month];
							return tm / (divisor > 0 ? divisor : 1);
						}
					}
				),
				fill: false,
				lineTension: 0,
				order: 1,
				hidden: false,
					stack: 'combine'
			});

			// Pasture Carryover (Pasture Type)
			datasets.push({
				yAxisID: "ySupply",
				label: "Carryover",
				backgroundColor: colors.other["Carryover"],
				borderColor: 'black', // colors.other["Carryover"],
				borderWidth: 0.5,
				pointStyle: 'rect',
      	pointBorderColor: 'rgba(0, 0, 0, 0.5)',
				data: carryOver.map((co: number, month: number) => {
					if (this.plotType === "tonnes/effective grazing area (total ha)") {
						return co;
					} else {
						let divisor = pasture.total.area[month];
						return co / (divisor > 0 ? divisor : 1);
					}
				}),
				fill: false,
				lineTension: 0,
				order: 1,
				hidden: false,
					stack: 'combine'
			});
		}

		// Supplement
		const supplementData = store.state.cattle
			? this.cattleCalcs.supplement.tonnesMonth
			: this.sheepCalcs.supplement.tonnesMonth;

		datasets.push({
			yAxisID: "ySupply",
			label: "Supplement",
			backgroundColor: colors.other["Supplement"],
			borderColor: 'black', // colors.other["Supplement"],
			borderWidth: 0.5,
			pointStyle: 'rect',
      pointBorderColor: 'rgba(0, 0, 0, 0.5)',
			data: supplementData.map((tpm: any, index: number) => {
				if (this.plotType === "tonnes/effective grazing area (total ha)") {
					return (tpm * (pasture.total.area[index] / store.state.pasture!.simulation.area));
				} else {
					return tpm / store.state.pasture!.simulation.area;
				}
			}),
			fill: false,
			lineTension: 0,
			order: 1,
			hidden: false,
			stack: 'combine'
		});

		return {
			labels: this.monthsInOrder.map(m => m.name),
			datasets: datasets
		};
	}

	mounted() {
		this.loading = true;
		setTimeout(() => {
			this.calculations = this.calcRef.common;
			this.update += 1;
			this.setKPIs();
			this.loading = false;
		}, 1000);
	}

	// Set KPI values
	setKPIs() {
		const denominator: number =
			this.plotType === "tonnes / ha"
				? store.state.pasture!.simulation.area
				: 1;
		const decimals: number = this.plotType === "tonnes / ha" ? 3 : 0;
		const unit: string =
			this.plotType === "tonnes / ha" ? "tonnes/ha/year" : "tonnes/year";

		let hasCattle:boolean = this.$store.state.pasture.simulation.enterprise.toLowerCase().includes('cattle')
		let hasSheep:boolean = this.$store.state.pasture.simulation.enterprise.toLowerCase().includes('sheep')

		const pastureDeficitFresh = this.calculations ? this.calculations.pastureKPI.deficitFresh : 0
		const pastureDeficitCarryover = this.calculations ? this.calculations.pastureKPI.deficitCarryover : 0
		const liveweightCattle = hasCattle ? this.cattleCalcs.liveWeightProducedKPI.tonnes : 0
		const liveweightCattlePerHa = hasCattle ? this.cattleCalcs.liveWeightProducedKPI.kgEnterprise : 0
		const liveweightSheep = hasSheep ? this.sheepCalcs.liveWeightProducedKPI.tonnes : 0
		const liveweightSheepPerHa = hasSheep ? this.sheepCalcs.liveWeightProducedKPI.kgEnterprise : 0
		const pastureDemand = this.calculations ? this.calculations.pastureKPI.demandAsPercentageOfPasture : 0
		const minimumFeedOnOffer = this.calculations ? this.calculations.pastureKPI.minimumFeedOnOffer : 0

		this.kpi.map((kpi: any) => {	
			switch (kpi.id) {
				case 'pastureDeficitFresh':
					kpi.value = pastureDeficitFresh
					? (pastureDeficitFresh / denominator).toFixed(decimals)
					: 0
					kpi.unit = unit;
					break;
				case 'pastureDeficitCarryover':
					kpi.value = pastureDeficitCarryover
						? (pastureDeficitCarryover / denominator).toFixed(decimals)
						: 0;
					kpi.unit = unit;
					break;
				case 'liveweightCattle':
					kpi.value = 
						!isNaN(liveweightCattle)
							? Math.round(liveweightCattle)
							: 0;
						break;
				case 'liveweightCattlePerHa':
					kpi.value = 
						!isNaN(liveweightCattlePerHa)
							? Math.round(liveweightCattlePerHa)
							: 0;
						break;
				case 'liveweightSheep':
					kpi.value = 
						!isNaN(liveweightSheep)
							? Math.round(liveweightSheep)
							: 0;
						break;
				case 'liveweightSheepPerHa':
					kpi.value = 
						!isNaN(liveweightSheepPerHa)
							? Math.round(liveweightSheepPerHa)
							: 0;
						break;
				case 'pastureDemand':
					kpi.value = 
						!isNaN(pastureDemand)
							? Math.round(pastureDemand)
							: 0;
						break;
				case 'minimumFeedOnOffer':
					kpi.value = 
						!isNaN(minimumFeedOnOffer)
							? Math.round(minimumFeedOnOffer)
							: 0;
						break;
				default:
					break;
			}
		})

		this.supplyHidden = false;
		this.demandHidden = false;

		this.update += 1;
	}

	// Format HSLA
	format({ h, s, l, a }: { h: number; s: number; l: number; a?: number }) {
		return `hsla(${h}, ${s}%, ${l}%, ${a || 1})`;
	}

	// Color HSLA
	color({ h, s, l, a }: { h: number; s: number; l: number; a?: number }) {
		return {
			value: this.format({
				h,
				s,
				l,
				a
			}),
			h,
			s,
			l,
			a,
			format: this.format
		};
	}

	// Generate random HSL color
	randomColor() {
		return this.color({
			h: Math.floor(Math.random() * 60) + 15,
			s: Math.floor(Math.random() * 50) + 25,
			l: Math.floor(Math.random() * 25) + 25,
			a: 1
		});
	}

	// Hide supply values (Feed Demand)
	hideSupplyValues() {
		if (this.demandHidden) {
			this.demandData.datasets.map((dd: Dataset) => {
				if (
					dd.label === "Fresh grown supply" ||
					dd.label === "Fresh supply + supplement" ||
					dd.label === "Total feed on offer"
				) {
					dd.hidden = true;
				}
			});
		} else {
			this.demandData.datasets.map((dd: Dataset) => {
				dd.hidden = false;
			});
		}
		this.update += 1;
	}

	// Show fresh supply only (Feed Supply)
	showFreshSupplyOnly() {
		if (this.supplyHidden) {
			this.supplyData.datasets.map((sd: Dataset) => {
				if (sd.label.indexOf("Carryover") >= 0) {
					sd.hidden = true;
				}
			});
		} else {
			this.supplyData.datasets.map((sd: Dataset) => {
				sd.hidden = false;
			});
		}
		this.update += 1;
	}

	// Get max range for charts
	getMax(chartData: any, precision: number) {
		let max = 0;
		let stacked: any[] = [];
		for (let i = 0; i < chartData.datasets.length; i++) {
			if (chartData.datasets[i].hidden) {
				continue;
			}
			if (chartData.datasets[i].yAxisID.includes("Stacked")) {
				if (stacked.length === 0) {
					stacked = chartData.datasets[i].data;
				} else {
					stacked = stacked.map((s: number, ix: number) => {
						return s + chartData.datasets[i].data[ix];
					});
				}
			}
			// All datasets
			chartData.datasets[i].data.forEach((d: number) => {
				if (d > max) {
					max = d;
				}
			});
			// Stacked
			stacked.forEach((d: number) => {
				if (d > max) {
					max = d;
				}
			});
		}
		// console.log(max)
		return Math.ceil(max / precision) * precision;
	}

	// Print page
	async print() {
		if (platform() == "web") {
			await print();
		} else {
			const scanvas: HTMLCanvasElement | null = document.getElementById(
				"supplyDemand"
			) as HTMLCanvasElement;
			if (scanvas && scanvas.parentElement) {
				const img = document.createElement("img");
				if (img) {
					img.src = scanvas.toDataURL();
					img.style.width = `${scanvas.clientWidth}px`;
					img.style.height = `${scanvas.clientHeight}px`;
					scanvas.parentElement.insertBefore(
						img,
						scanvas.nextSibling
					);
					const previousDisplay = scanvas.style.display;
					scanvas.style.display = "none";

					await print();

					if (scanvas && img.parentElement) {
						img.parentElement.removeChild(img);
						scanvas.style.display = previousDisplay;
					}
				} else {
					await print();
				}
			} else {
				await print();
			}
		}
	}
}
